import React, { useEffect, useState } from 'react';
import { CheckCircle2, Printer, Copy, Check, ShieldCheck, ArrowLeft } from 'lucide-react';
import { sendPaymentSuccessEmail } from '../utils/emailService';
import { getLatestApplicationData, saveApplicationData } from '../utils/applicationService';

export default function PaymentSuccessPage({ appData, onReturnHome }) {
  const [copied, setCopied] = useState(false);
  const [currentAppData, setCurrentAppData] = useState(appData || null);

  useEffect(() => {
    // Determine Application Data from Props, sessionStorage, or URL parameters
    let data = appData;

    if (!data) {
      data = getLatestApplicationData();
    }

    const params = new URLSearchParams(window.location.search);
    const urlTxnId = params.get('txnid') || params.get('mihpayid');
    const urlAppNo = params.get('appno');
    const urlName = params.get('name');
    const urlEmail = params.get('email');
    const urlPhone = params.get('phone');
    const urlCourse = params.get('course');

    if (!data || (urlAppNo && data.appNumber !== urlAppNo)) {
      data = {
        appNumber: urlAppNo || `Christ-University-27001`,
        txnId: urlTxnId || `PAYU_${Date.now()}`,
        fullName: urlName ? decodeURIComponent(urlName) : (data?.fullName || 'Registered Applicant'),
        email: urlEmail ? decodeURIComponent(urlEmail) : (data?.email || 'applicant@formsadda.com'),
        phone: urlPhone ? decodeURIComponent(urlPhone) : (data?.phone || '9876543210'),
        course: urlCourse ? decodeURIComponent(urlCourse) : (data?.course || 'Master of Science in Data Science (M.Sc DS)'),
        city: data?.city || 'Bangalore, Karnataka',
        amount: '₹500',
        paymentStatus: 'SUCCESS',
        formattedDate: new Date().toLocaleString('en-IN', {
          dateStyle: 'full',
          timeStyle: 'medium'
        })
      };
      saveApplicationData(data);
    }

    setCurrentAppData(data);

    // Dispatch email notifications to User & Admin (admin@formsadda.com)
    if (data && !data.emailSent) {
      sendPaymentSuccessEmail(data);
      data.emailSent = true;
      saveApplicationData(data);
    }
  }, [appData]);

  const handleCopyAppNo = () => {
    if (currentAppData?.appNumber) {
      navigator.clipboard.writeText(currentAppData.appNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  if (!currentAppData) {
    return (
      <div style={{ padding: '4rem 1rem', textAlign: 'center', backgroundColor: '#07131E', color: '#FFFFFF', minHeight: '100vh' }}>
        <h2>Loading Payment Success Details...</h2>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh', padding: '2rem 1rem 5rem 1rem', color: '#0F172A' }}>
      
      {/* Printable CSS Rules */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-invoice, #printable-invoice * {
            visibility: visible;
          }
          #printable-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
            margin: 0;
            box-shadow: none !important;
            border: 2px solid #0F2537 !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div className="container" style={{ maxWidth: '840px', margin: '0 auto' }}>
        
        {/* Navigation Bar / Return Link */}
        <div className="no-print" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={onReturnHome}
            style={{
              background: 'none',
              border: 'none',
              color: '#0F2537',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.92rem'
            }}
          >
            <ArrowLeft size={18} /> Back to University Homepage
          </button>
          
          <span style={{ fontSize: '0.82rem', color: '#059669', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
            <ShieldCheck size={16} /> PayU SSL Verified Transaction
          </span>
        </div>

        {/* Success Alert Banner */}
        <div className="no-print" style={{
          backgroundColor: '#065F46',
          color: '#FFFFFF',
          borderRadius: '20px',
          padding: '2rem',
          marginBottom: '2rem',
          textAlign: 'center',
          boxShadow: '0 15px 35px rgba(6, 95, 70, 0.25)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '72px',
            height: '72px',
            backgroundColor: '#10B981',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem auto',
            boxShadow: '0 0 25px rgba(16, 185, 129, 0.5)'
          }}>
            <CheckCircle2 size={44} color="#FFFFFF" />
          </div>

          <span style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: '#FEF08A',
            fontWeight: 800,
            fontSize: '0.8rem',
            padding: '0.3rem 0.85rem',
            borderRadius: '9999px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            Registration Fee Verified
          </span>

          <h1 style={{ fontSize: '2.2rem', fontWeight: 800, margin: '0.75rem 0 0.4rem 0', letterSpacing: '-0.02em', color: '#FFFFFF' }}>
            Application Submitted Successfully!
          </h1>
          <p style={{ fontSize: '1.05rem', color: '#E2E8F0', maxWidth: '600px', margin: '0 auto 1.5rem auto', lineHeight: 1.5 }}>
            Thank you <strong>{currentAppData.fullName}</strong>. Your online admission application for CHRIST (Deemed to be University) has been registered.
          </p>

          {/* Unique Application Number Highlight Badge */}
          <div style={{
            backgroundColor: '#07131E',
            border: '2px solid #D4AF37',
            borderRadius: '16px',
            padding: '1.25rem 1.5rem',
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.5rem',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
          }}>
            <span style={{ fontSize: '0.8rem', color: '#CBD5E1', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
              Unique Application Reference Number
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <span style={{ fontSize: '1.65rem', fontWeight: 900, color: '#FEF08A', letterSpacing: '0.04em', fontFamily: 'monospace' }}>
                {currentAppData.appNumber}
              </span>
              <button
                onClick={handleCopyAppNo}
                style={{
                  backgroundColor: copied ? '#059669' : '#D4AF37',
                  color: '#0F172A',
                  border: 'none',
                  padding: '0.4rem 0.85rem',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  transition: 'all 0.2s'
                }}
              >
                {copied ? <Check size={15} /> : <Copy size={15} />}
                {copied ? 'Copied!' : 'Copy App No.'}
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons: Download PDF & Return */}
        <div className="no-print" style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <button
            onClick={handlePrintInvoice}
            className="btn-primary"
            style={{ padding: '0.85rem 1.6rem', fontSize: '0.95rem' }}
          >
            <Printer size={18} /> Print / Download Official Invoice (PDF)
          </button>
          
          <button
            onClick={onReturnHome}
            className="btn-secondary"
            style={{ padding: '0.85rem 1.6rem', fontSize: '0.95rem' }}
          >
            Return to University Portal
          </button>
        </div>

        {/* OFFICIAL INVOICE / RECEIPT DOCUMENT CARD (Printable) */}
        <div
          id="printable-invoice"
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '20px',
            border: '1.5px solid #CBD5E1',
            boxShadow: '0 20px 40px rgba(15, 37, 55, 0.08)',
            padding: '2.5rem',
            color: '#0F172A'
          }}
        >
          {/* Invoice Header */}
          <div style={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'flex-start',
            borderBottom: '2px solid #0F2537',
            paddingBottom: '1.5rem',
            marginBottom: '1.75rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <div style={{
                height: '46px',
                backgroundColor: '#0F2537',
                borderRadius: '8px',
                padding: '4px 12px',
                display: 'inline-flex',
                alignItems: 'center',
                marginBottom: '0.75rem'
              }}>
                <img 
                  src="/uploads/logo.png" 
                  alt="CHRIST (Deemed to be University)" 
                  style={{ height: '36px', objectFit: 'contain' }}
                />
              </div>
              <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0F2537', margin: 0 }}>
                CHRIST (Deemed to be University)
              </h2>
              <p style={{ fontSize: '0.82rem', color: '#64748B', margin: '0.2rem 0 0 0' }}>
                UGC Entitled & NAAC 'A+' Grade Online Programmes • Admissions 2026
              </p>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div style={{
                backgroundColor: '#F1F5F9',
                border: '1px solid #CBD5E1',
                padding: '0.5rem 1rem',
                borderRadius: '10px',
                display: 'inline-block'
              }}>
                <div style={{ fontSize: '0.72rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 700 }}>
                  OFFICIAL ADMISSION RECEIPT
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#0F2537', fontFamily: 'monospace' }}>
                  {currentAppData.appNumber}
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748B', marginTop: '0.5rem' }}>
                Date: {currentAppData.formattedDate}
              </div>
            </div>
          </div>

          {/* Candidate & Payment Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            marginBottom: '1.75rem'
          }}>
            {/* Candidate Info Box */}
            <div style={{
              backgroundColor: '#F8FAFC',
              borderRadius: '14px',
              padding: '1.25rem',
              border: '1px solid #E2E8F0'
            }}>
              <h4 style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0F2537', marginBottom: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                👤 Candidate Particulars
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', fontSize: '0.88rem' }}>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>Full Name:</span> <strong>{currentAppData.fullName}</strong></div>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>Mobile No:</span> <strong>+91 {currentAppData.phone}</strong></div>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>Email ID:</span> <strong>{currentAppData.email}</strong></div>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>City/State:</span> <strong>{currentAppData.city}</strong></div>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>Programme:</span> <strong style={{ color: '#0F2537' }}>{currentAppData.course}</strong></div>
              </div>
            </div>

            {/* Payment Summary Box */}
            <div style={{
              backgroundColor: '#F8FAFC',
              borderRadius: '14px',
              padding: '1.25rem',
              border: '1px solid #E2E8F0'
            }}>
              <h4 style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0F2537', marginBottom: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                💳 Payment Transaction Details
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem', fontSize: '0.88rem' }}>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>Payment Gateway:</span> <strong>PayU Official Merchant</strong></div>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>Transaction Ref:</span> <strong style={{ fontFamily: 'monospace' }}>{currentAppData.txnId}</strong></div>
                <div>
                  <span style={{ color: '#64748B', fontWeight: 600 }}>Payment Status:</span>{' '}
                  <span style={{ backgroundColor: '#D1FAE5', color: '#047857', fontWeight: 800, padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem' }}>
                    SUCCESS / VERIFIED
                  </span>
                </div>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>Merchant ID:</span> <strong>3bqRWp (PayU)</strong></div>
                <div><span style={{ color: '#64748B', fontWeight: 600 }}>Fee Purpose:</span> <strong>Application Registration Token</strong></div>
              </div>
            </div>
          </div>

          {/* Fee Breakdown Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#0F2537', color: '#FFFFFF', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 1rem', borderRadius: '8px 0 0 8px' }}>Description</th>
                <th style={{ padding: '0.75rem 1rem' }}>Category</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'right', borderRadius: '0 8px 8px 0' }}>Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                <td style={{ padding: '0.85rem 1rem', fontWeight: 700 }}>
                  Online Programme Registration & Document Verification Fee
                </td>
                <td style={{ padding: '0.85rem 1rem', color: '#64748B' }}>UGC Online Application</td>
                <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 700 }}>₹500.00</td>
              </tr>
              <tr style={{ backgroundColor: '#F8FAFC', fontWeight: 800 }}>
                <td colSpan={2} style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>Total Amount Paid:</td>
                <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: '#059669', fontSize: '1.25rem' }}>₹500.00</td>
              </tr>
            </tbody>
          </table>

          {/* Invoice Footer Seal & Signature */}
          <div style={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #CBD5E1',
            paddingTop: '1.5rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <div style={{ fontSize: '0.78rem', color: '#64748B', lineHeight: 1.5 }}>
                ✔ Computer Generated Receipt. No physical signature required.<br />
                ✔ Copy sent to applicant email (<strong>{currentAppData.email}</strong>) and Admissions Admin (<strong>admin@formsadda.com</strong>).
              </div>
            </div>

            <div style={{ textAlign: 'center' }}>
              <div style={{
                border: '2px solid #059669',
                color: '#059669',
                fontWeight: 900,
                fontSize: '0.8rem',
                padding: '0.35rem 0.85rem',
                borderRadius: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '0.3rem'
              }}>
                OFFICIALLY VERIFIED
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748B' }}>
                FormsADDA Admission Portal
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
